import { useEffect, useMemo, useState } from 'react';
import { GEOLOCATION_PERMISSION } from '../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { setGps } from '../store/reducers/gps';
import useGoogleMapsLoader from '../hook/useGoogleMapsLoader';
import { getCurrentLocation, getDetailLocationFromLatLong, isEmptyData, loggingGps, roundCoordinate } from '../utils/helper';
import { setUser } from '../store/reducers/user';
import useGeolocationPermission from './useGeolocationPermission';
import { getRecentList, getSavedList } from 'store/reducers/location';

const useCurrentLocation = () => {
	useGeolocationPermission();
	const { isLoaded: isLoadedGoogleMaps } = useGoogleMapsLoader();

	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);

	const queryParams = new URLSearchParams(window.location.search);
	const latitude = queryParams.get('latitude');
	const longitude = queryParams.get('longitude');

	const user = useSelector((store) => store?.user);
	const { savedData: savedArress, recentData: recentAddress, savedLoading: isLoadingSavedAddress, recentLoading: isLoadingRecentAddress } = useSelector((store) => store?.location);

	const { permission } = useSelector((store) => store?.gps);

	const fetchLocation = async () => {
		try {
			const loc = await getCurrentLocation();
			const parsedCoords = {
				latitude: roundCoordinate(loc?.latitude),
				longitude: roundCoordinate(loc?.longitude),
				accuracy: loc?.accuracy,
				altitude: loc?.altitude,
				altitudeAccuracy: loc?.altitudeAccuracy,
				heading: loc?.heading,
				speed: loc?.speed,
			};
			const log = loggingGps({ message: 'Location retrieved successfully' }, parsedCoords, isLoadedGoogleMaps, permission);
			dispatch(setGps({ coords: parsedCoords, logs: log }));
			return parsedCoords;
		} catch (err) {
			const log = loggingGps(err, null, isLoadedGoogleMaps, permission);
			dispatch(setGps({ logs: log }));
			return null;
		}
	};

	const handleGetAddressFromLatLon = async (latitude, longitude) => {
		if (!latitude || !longitude) return;
		try {
			const formated = await getDetailLocationFromLatLong(latitude, longitude);
			dispatch(
				setUser({
					address: formated?.address,
					latitude: formated?.lat,
					longitude: formated?.lng,
					country: formated?.country,
					state: formated?.state,
					city: formated?.city,
					village: formated?.village,
					district: formated?.district,
					postalCode: formated?.postalCode,
					detail: null,
				}),
			);
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const handleGetAddressFromSavedAddress = (primaryAddress = null) => {
		try {
			if (!primaryAddress) return;

			const address = {
				address: primaryAddress?.address,
				latitude: primaryAddress?.latitude,
				longitude: primaryAddress?.longitude,
				country: primaryAddress?.country,
				state: primaryAddress?.state,
				city: primaryAddress?.city,
				village: primaryAddress?.village,
				district: primaryAddress?.district,
				postalCode: primaryAddress?.postalCode,
				detail: primaryAddress?.address_detail || null,
			};
			dispatch(
				setUser({
					...address,
				}),
			);
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const selectLocation = useMemo(() => {
		const isPrimary = (address) => String(address?.primary) === '1';
		const asArray = (list) => (Array.isArray(list) ? list : []);
		const primary = [...asArray(savedArress), ...asArray(recentAddress)].find(isPrimary);
		if (!isEmptyData(primary)) {
			return { type: 'SAVED', data: primary };
		}

		if (!isEmptyData(latitude) && !isEmptyData(longitude)) {
			return { type: 'PARAMS', data: { latitude, longitude } };
		}

		if (isEmptyData(permission)) {
			return { type: 'PASS', data: null };
		}

		if (permission === GEOLOCATION_PERMISSION.GRANTED) {
			return { type: 'GPS', data: null };
		}

		return { type: 'PASS', data: null };
	}, [latitude, longitude, savedArress, recentAddress, permission]);

	const handleFetchAndGeocode = async (cancelledRef) => {
		const newCoords = await fetchLocation();
		if (cancelledRef?.current || !newCoords?.latitude || !newCoords?.longitude) return;
		await handleGetAddressFromLatLon(newCoords.latitude, newCoords.longitude);
	};

	useEffect(() => {
		if (isEmptyData(user?.token)) return;
		dispatch(getRecentList());
		dispatch(getSavedList());
	}, [user?.token]);

	// Tidak mengubah logic asli — cuma menambahkan setIsLoading di titik mulai & selesai
	useEffect(() => {
		if (isLoadingSavedAddress || isLoadingRecentAddress) return;
		if (!isEmptyData(user?.address)) return;
		if (selectLocation.type !== 'SAVED') return;

		console.log('SAVED');
		handleGetAddressFromSavedAddress(selectLocation.data);
	}, [selectLocation, user?.address]);

	useEffect(() => {
		if (isLoadingSavedAddress || isLoadingRecentAddress) return;
		if (!isEmptyData(user?.address)) return;
		if (selectLocation.type !== 'PARAMS') return;

		console.log('PARAMS');

		let cancelled = false;
		setIsLoading(true);

		handleGetAddressFromLatLon(selectLocation.data.latitude, selectLocation.data.longitude).finally(() => {
			if (!cancelled) setIsLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [selectLocation, user?.address]);

	useEffect(() => {
		if (isLoadingSavedAddress || isLoadingRecentAddress) return;
		if (!isEmptyData(user?.address)) return;
		if (selectLocation.type !== 'GPS') return;

		console.log('GPS');
		const cancelledRef = { current: false };
		setIsLoading(true);

		handleFetchAndGeocode(cancelledRef).finally(() => {
			if (!cancelledRef.current) setIsLoading(false);
		});

		return () => {
			cancelledRef.current = true;
		};
	}, [selectLocation, user?.address]);

	return { isLoading };
};

export default useCurrentLocation;
